import moment from 'moment';
import {
    getDocumentNotes,
    getEmployeeByID,
    getNumberOfArticles,
    getProductsInBasket,
    getProductsTotalSum,
    getReceiptLanguage,
    getTillNumber,
    getVatRateByID
} from "../redux/selectors";
import {getCardPayments} from "../redux/payment/selectors";
import html2canvas from "html2canvas";
import bwipjs from 'bwip-js';
import {APP_URL} from "../redux/appConstants";
import {base64Encode, displayPrice as formatDisplayPrice} from "./helperFunctions";
import {getTranslate} from "localization/localizeSlice";
import * as Sentry from "@sentry/react";

/**
 * Create transaction receipt
 * Dependencies: momentjs, htmlToText
 * Run CTR.init to set parameters
 */

let CTR = {
    numberPrecision: 2,
    receiptWidth: () => {
        let defaultWidth = 300;
        if (typeof window?.AppConf?.receiptWidth !== "undefined") {
            let width = parseInt(window?.AppConf?.receiptWidth);
            return isNaN(width) ? defaultWidth : width;
        }
        return defaultWidth;
    },
    displayQRCode: () => window?.AppConf?.qrCodeOnDecathlonReceipt || false,
    dateFormatInReceiptNumber: () => window?.AppConf?.dateFormatInDecathlonReceiptNumber || 'DDMMYYYYHHmmss',
    getReceiptTypeTitle: (type) => {
        let title = window?.AppConf?.receiptTypeTitle?.[type.toLowerCase()] ?? "";
        return title === "" ? type : title;
    },
    displayProductNetPrice: false,
    logoBase64: "data:image/png;base64,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",
    currencySign: '',
    receiptPrinted: false,
    positiveReturnLines: false,
    fiscalDataPosition: 'up',
    lastReceipt: false,
    lastReceiptNumber: false,
    init: (state) => {
        CTR.currencySign = state.rootReducer.currency;
        CTR.country = state.rootReducer.erplyConf.country;
        CTR.clientCode = state.rootReducer.user.clientCode;
        CTR.sessionKey = state.rootReducer.user.sessionKey;
        CTR.emailAPIEndpoint = state.rootReducer.erplyServiceEndpoints?.ems.url;
        if(CTR.country === 'CA'){
            CTR.displayProductNetPrice = true;
        }else if(CTR.country === 'DE'){
            CTR.applyDESpecifics();
        }
        CTR.language = getReceiptLanguage(state);
        CTR.appTranslate = getTranslate(state);
    },
    companyInformation: function(companyName, storeName, storeAddress, storePostalCode, storeCity, storeTelephone, companyTaxID, openingHours){
        this.companyName = companyName;
        this.storeName = storeName;
        this.storeAddress = storeAddress;
        this.storePostalCode = storePostalCode;
        this.storeCity = storeCity;
        this.storeTelephone = storeTelephone;
        this.companyTaxID = companyTaxID;
        this.openingHours = openingHours;
    },
    translate: (text, lang=CTR.language) => {
        if(lang === 'fr' && CTR.country === 'CA'){
            return CTR.translations?.[text]?.['fr-ca'] || CTR.translations?.[text]?.[lang] || CTR.appTranslate(text, null, {language: 'fr_ca'});
        }
        let appLangName = lang;
        if(lang === 'es' && CTR.country === 'CO'){
            appLangName = 'es_co';
        }
        if(lang === 'es' && CTR.country === 'CL'){
            appLangName = 'es_cl';
        }
        if(lang === 'es' && CTR.country === 'PY'){
            return CTR.appTranslate(text, null, {language: 'es_py'});
        }
        if(lang === 'nl' && CTR.country === 'BE'){
            appLangName = 'nl_be';
        }
        return CTR.translations?.[text]?.[lang] || CTR.appTranslate(text, null, {language: appLangName});
    },
    translations: {
        'Tel': {
            'es': 'Teléfono',
            'de': 'Telefon',
            'fr': 'Téléphone',
            'nl': 'Tel',
            'se': 'Tele',
            'cz': 'Telefon'
        },
        'VAT No': {
            'es': 'IVA no.',
            'de': 'VAT-Nr. / UID',
            'fr': 'No de taxes',
            'nl': 'BTW Nr',
            'se': 'Momsnummer',
            'cz': 'DIČ'
        },
        'Sale': {
            'es': 'Venta',
            'de': 'Verkauf',
            'fr': 'Ticket de vente',
            'nl': 'Verkoop',
            'se': 'Köp',
            'cz': 'Prodej'
        },
        'Refund': {
            'es': 'Devolución',
            'de': 'Rückerstattung',
            'fr': 'Ticket de retour',
            'nl': 'Retour',
            'se': 'Retur',
            'cz': 'Vrácení'
        },
        'Duplicate': {
            'es': 'Duplicado',
            'de': 'Duplikat',
            'fr': 'Duplicata',
            'nl': 'Duplicata',
            'fr-ca': 'Dupliquer',
            'se': 'Duplicera',
            'cz': 'Duplikát'
        },
        'Gift': {
            'es': 'Regalo',
            'de': 'Geschenk',
            'fr': 'Cadeau',
            'nl': 'Cadeau',
            'se': 'Tillgodokvitto',
            'cz': 'DÁRKOVÁ ÚČTENKA'
        },
        'Card number': {
            'es': 'Número de tarjeta',
            'de': 'Kartennummer',
            'fr': 'Numéro de carte',
            'nl': 'Kaart Nummer',
            'se': 'Kortnummer',
            'cz': 'Číslo karty'
        },
        'RFID': {
            'es': 'RFID',
            'de': 'RFID',
            'fr': 'RFID',
            'nl': 'RFID',
            'se': 'RFID',
            'cz': 'RFID'
        },
        'Total': {
            'es': 'Total',
            'de': 'Gesamt',
            'fr': 'Total',
            'nl': 'Totaal',
            'se': 'Total',
            'cz': 'Celkem'
        },
        'Tax': {
            'es': '% de IVA',
            'de': '% MwSt.',
            'fr': 'TAUX',
            'nl': 'BTW',
            'fr-ca': '% de taxes',
            'se': 'Moms%',
            'cz': '% DPH'
        },
        'Tax total': {
            'es': 'IVA',
            'de': 'MwSt. Gesamt',
            'fr': 'MTT TVA',
            'nl': 'Bedrag BTW',
            'fr-ca': 'Total taxes',
            'se': 'Moms',
            'cz': 'Celkem DPH'
        },
        'Net total': {
            'es': 'Neto total',
            'de': 'Nettosumme',
            'fr': 'MTT HT',
            'nl': 'Excl. BTW',
            'fr-ca': 'Total net',
            'se': 'Netto',
            'cz': 'Celkem bez DPH'
        },
        'Till': {
            'es': 'Caja',
            'de': 'Kasse',
            'fr': 'Caisse',
            'nl': 'Kassa',
            'se': 'Kassa',
            'cz': 'Pokladna'
        },
        'Employee': {
            'es': 'Empleado',
            'de': 'Mitarbeiter',
            'fr': 'Employé',
            'nl': 'Personeel',
            'se': 'Kassör',
            'cz': 'Zaměstnanec'
        },
        'Store': {
            'es': 'Tienda',
            'de': 'Geschäft',
            'fr': 'Magasin',
            'nl': 'Winkel',
            'se': 'Butik',
            'cz': 'Prodejna'
        },
        'Amount Paid': {
            'es': 'Cantidad pagada',
            'de': 'Bezahlt',
            'fr': 'Payé',
            'nl': 'Betaald Bedrag',
            'se': 'Totalbelopp',
            'cz': 'Zaplacená částka'
        },
        'Card Type': {
            'es': 'Tipo de tarjeta',
            'de': 'Kartenart',
            'fr': 'Type de carte',
            'nl': 'Kaart type',
            'se': 'Kort typ',
            'cz': 'Typ karty'
        },
        'Card #': {
            'es': 'Tarjeta #',
            'de': 'Kartennummer',
            'fr': 'Carte #',
            'nl': 'Kaart #',
            'se': 'Kort #',
            'cz': 'Číslo karty'
        },
        'Expires': {
            'es': 'Expira',
            'de': 'Gültig bis',
            'fr': 'Expire',
            'nl': 'Verlopen',
            'se': 'Utgångsdatum',
            'cz': 'Datum platnosti'
        },
        'Ref #': {
            'es': 'Ref #',
            'de': 'Ref #',
            'fr': 'Ref #',
            'nl': 'Ref #',
            'se': 'REF-NR',
            'cz': 'Ref #'
        },
        'Authorization code': {
            'es': 'Codigo de aprobacion',
            'de': 'Authorisierungscode',
            'fr': 'Code d\'approbation',
            'nl': 'Authorisatie code',
            'se': 'AID',
            'cz': 'Autorizační kód'
        },
        'Can\'t print the receipt': {
            'es': 'Impresion fallida',
            'de': 'Druck fehlgeschlagen!',
            'fr': 'Impression échouée',
            'nl': 'Kan de bon niet afdrukken',
            'se': 'Kan inte skriva ut kvittot',
            'cz': 'Nelze vytisknout'
        },
        'Article': {
            'es': 'Artículo',
            'de': 'Artikel',
            'nl': 'Artikel',
            'se': 'Artikel',
            'cz': 'Položka'
        },
        'Articles': {
            'es': 'Artículos',
            'de': 'Artikel',
            'nl': 'Artikelen',
            'se': 'Artiklar',
            'cz': 'Položky'
        },
        'Code': {
            'es': 'Código',
            'se': 'Kod',
            'cz': 'Kód'
        },
        'changeMoneySum': {
            'en': 'Change',
            'es': 'Cambio',
            'fr': 'Rendu monnaie',
            'de': 'Rückgeld',
            'se': 'Ändra',
            'pl': 'Reszta'
        },
        'CASH': {
            'es': 'Efectivo',
            'fr': 'Espèces',
            'de': 'Bar',
            'se': 'Kontanter'
        },
        'CARD': {
            'es': 'Tarjeta',
            'de': 'Karte',
            'se': 'Kort',
        },
        'Manual Card': {
            'es': 'Tarjeta Manual',
            'de': 'Manuelle Karte',
            'se': 'Manuellt kort',
        },
        'Deferred Payment': {
            'es': 'Pago diferido',
            'de': 'Zahlungsaufschub',
            'se': 'Fakturabetalning',
        },
        'GIFTCARD': {
            'es': 'Tarjeta Regalo',
            'de': 'Geschenkkarte',
            'se': 'Presentkort',
        }
    },
    createReceipt: (
        companyInformation,
        receiptType,
        fiscalData,
        customer,
        products,
        total,
        payments,
        tillNumber,
        date,
        time,
        employee,
        storeNumber,
        receiptNumber,
        receiptFooter,
        eftInfo,
        notes,
        confParameters,
        documentDiscount,
        vatRates,
        extraLines,
        usedProductCustomer
    ) => {
        return new Promise((resolve, reject) => {
            let uniqueReceiptNumber = CTR.createUniqueReceiptNumber(storeNumber, tillNumber, date, time, receiptNumber);
            CTR.getBarcodeLine(uniqueReceiptNumber).then((barcodeLine) => {
                let html = `\
                    <html lang="${CTR.language}">\
                        <head>\
                            <title>Receipt</title>\
                            <style>\
                                td{\
                                    padding: 0;\
                                }\
                                p{\
                                    margin: 0;\
                                }\
                                table{\
                                    font-size: inherit;\
                                }\
                                .solidLine{\
                                    width: 100%;\
                                    height: 1px;\
                                    background: #000;\
                                    border-bottom: 1px solid #000;\
                                }\
                                body{\
                                    padding: 0;\
                                    max-width: ${CTR.receiptWidth()}px;\
                                    font-family: "Roboto Condensed", sans-serif;\
                                    font-size:10px;\
                                    font-variant: normal;\
                                }\
                                pre{\
                                   font-family: "Roboto Condensed", sans-serif;\
                                   font-size: 10px;\
                                }\
                            </style>\
                        </head>\
                        <body>\
                    `;
                html += CTR.getLogo(CTR.logoBase64);
                html += CTR.getCompanyLines(companyInformation, confParameters);
                if(CTR.fiscalDataPosition === 'up'){
                    html += CTR.getFiscalDataLine(fiscalData);
                }
                if(typeof customer !== "undefined"){
                    html += CTR.getCustomerLine(customer);
                }
                if(usedProductCustomer !== false){
                    html += CTR.getUsedProductCustomerLines(usedProductCustomer);
                }
                html += CTR.getReceiptTypeLine(receiptType);
                html += CTR.getSolidLine();
                html += CTR.getProductLines(products);
                html += CTR.getSolidLine();
                html += CTR.getTotalLine(total, getNumberOfArticles(products), documentDiscount.percentage, documentDiscount.price);
                html += CTR.getPaymentLines(payments);
                html += CTR.getSolidLine();
                html += CTR.getTaxLines(products, vatRates);
                html += CTR.getSolidLine();
                if(notes !== ''){
                    html += `<pre style="max-width: 100%;white-space: pre-wrap;word-wrap: break-word;">${notes}</pre><br/>`;
                }
                html += CTR.getPOSInformationLines(tillNumber, date, time, employee, storeNumber, receiptNumber);
                html += barcodeLine;
                html += CTR.getReceiptNumberLine(uniqueReceiptNumber);
                html += `<br/><pre style="white-space: pre-wrap;word-wrap: break-word;">${receiptFooter}</pre>`;
                html += CTR.getAfterFooterLines();
                if(typeof eftInfo !== "undefined"){
                    console.log({eftInfo});
                    html += CTR.getSolidLine();
                    html += `<div id="cardPayments">${eftInfo}</div>`;
                }
                if(CTR.fiscalDataPosition === 'down'){
                    html += '<br/>'  + CTR.getFiscalDataLine(fiscalData);
                }
                extraLines.forEach(data => {
                    html += '<br/>' + data;
                });
                html += '</body></html>';
                resolve({receiptNumber: uniqueReceiptNumber, receipt: html});
            });
        });
    },
    getAfterFooterLines: () => {
        return '';
    },
    getSolidLine: () => {
        return `<div class="solidLine"></div>`;
    },
    getLogo: (logo) => {
        return `\
            <div id="companyLogo" style="margin-bottom: 10px;">\
                <img alt="companyLogo" src="${logo}" width="200" style="display: block;margin: 0 auto;"/>\
            </div>\
        `;
    },
    getCompanyLines: (companyInformation, confParameters) => {
        let vatNoLine = `<div>${CTR.translate('VAT No')}: ${companyInformation.companyTaxID}</div>`;
        if(typeof confParameters.DKTVatNumber1 !== "undefined" && confParameters.DKTVatNumber1 !== ""){
            vatNoLine = `\
                <div>${confParameters.DKTVatNumber1}</div>\
                <div>${confParameters.DKTVatNumber2 || ''}</div>\
            `;
        }
        return `\
            <div\
                id="companyInfo"\
                style="text-align: center;"\
            >\
                <div>${companyInformation.companyName}</div>\
                <div>${companyInformation.storeName}</div>\
                <div>${companyInformation.storeAddress}</div>\
                <div>${companyInformation.storePostalCode} ${companyInformation.storeCity}</div>\
                <div>${CTR.translate('Tel')}: ${companyInformation.storeTelephone}</div>\
                ${vatNoLine}\
                <div>${companyInformation.openingHours}</div>\
            </div>\
        `;
    },
    getReceiptTypeLine: (receiptType) => {
        return `<br/><br/><div id="receiptType" style="text-align: center;text-transform: uppercase; font-size: 12px; font-weight: bold;">${CTR.translate(CTR.getReceiptTypeTitle(receiptType))}</div><br/><br/>`;
    },
    getReceiptNumberLine: (number) => {
        return `<div id="receiptNumber" style="text-align: center;">${number}</div>`;
    },
    changeReceiptType: (receipt, receiptType) => {
        let parser = new DOMParser();
        let doc = parser.parseFromString(receipt, 'text/html');
        doc.getElementById('receiptType').innerText = CTR.translate(CTR.getReceiptTypeTitle(receiptType),doc.documentElement.lang);
        return doc.documentElement.outerHTML;
    },
    getFiscalDataLine: (fiscalData) => {
        if(fiscalData !== false){
            return `<br/><div>${fiscalData}</div>`;
        }
        return '';
    },
    getCustomerLine: (customer) => {
        if(customer.card_number !== ''){
            return `<br/><div>${CTR.translate('Card number')}: ${customer.card_number}</div>`;
        }else if(typeof customer.email !== "undefined"){
            return `<br/><div>${CTR.translate('Customer')}: ${customer.email}</div>`;
        }else if(typeof customer.phone !== "undefined"){
            return `<br/><div>${CTR.translate('Customer')}: ${customer.phone}</div>`;
        }
        return '';
    },
    getUsedProductCustomerLines: (customer) => {
        return `<br/>
            <div>${CTR.translate('Customer Name')}: ${customer.customerName}</div>
            <div>${CTR.translate('Customer Address')}: ${customer.customerAddress}</div>
            <div>${CTR.translate('Customer City')}: ${customer.customerCity}</div>
        `;
    },
    getProductLines: (products) => {
        return `\
            <table style="width: 100%;margin: 2px 0;">\
                <tbody>\
                    ${products.map((product) => {
                        return CTR.createItemLine(product);
                    }).join('')}\
                </tbody>\
            </table>\
        `;
    },
    createItemLine: (product) => {
        let rfid = product?.data?.RFIDTag?.SERIAL_NUMBER || product?.data?.voucherRFIDTag?.SERIAL_NUMBER || product?.data?.RFIDSerialNumber || '';
        if(rfid === 'NULL_VALUE') rfid = '';
        if(typeof product.giftCardCode !== 'undefined'){
            rfid = '';
        }
        let rfidLine = '';
        if(rfid !== ''){
            rfidLine = `${CTR.translate('RFID')}: ${rfid}`;
        }
        let rowTotal = CTR.displayProductNetPrice ? parseFloat(product.rowNetTotal) : parseFloat(product.rowTotal);
        let rowUnitPrice = CTR.displayProductNetPrice ? parseFloat(product.netPrice) : parseFloat(product.vatPrice);

        if(CTR.positiveReturnLines){
            rowTotal = Math.abs(rowTotal);
        }
        let regularPrice = "";
        let discount = "";
        if(product.discount !== 0){
            let rowRegularPrice = CTR.displayProductNetPrice ? parseFloat(product.originalPrice) : parseFloat(product.originalPriceWithVAT);
            regularPrice = CTR.translate('Regular price') + ': ' + CTR.displayPrice(rowRegularPrice);
            discount = product.discount * -1 + "%";
        }
        let quantity = "";
        let unitPrice = "";
        if(Math.abs(product.amount) !== 1){
            quantity = product.amount + (product.data.unitName || CTR.translate('pcs'));
            unitPrice = CTR.displayPrice(rowUnitPrice);
        }
        let usedProductInfoLine = product.isUsedProduct() ?
            `<tr><td>${CTR.translate(window?.AppConf?.marginSchemeText ?? 'Margin scheme - second hand goods')}</td></tr>` :
            '';
        return `\
            ${usedProductInfoLine}
            <tr>\
                <td>${product.name}</td>\
                <td class="itemPrice">${regularPrice}</td>\
                <td></td>\
            </tr>\
            <tr>\
                <td>${product.codeInfo || ''}${product.data.code}</td>\
                <td>${quantity} <span class="itemPrice">${discount} ${unitPrice}</span></td>\
                <td class="itemPrice" style="text-align: right">${CTR.displayPrice(rowTotal.toFixed(CTR.numberPrecision))}</td>\
            </tr>\
            <tr>\
                <td>${rfidLine}</td>\
            </tr>\
        `;
    },
    getPaymentLines: (payments) => {
        return `\
            <br/>\
            <table id="payments" style="width: 100%;">\
                <tbody>\
                    ${payments.map((payment) => {
                        return CTR.createPaymentLine(payment);
                    }).join('')}\
                </tbody>\
            </table>\
        `;
    },
    createPaymentLine: (payment) => {
        let extraInfo = '';
        if(typeof payment.extraInfo === "string"){
            extraInfo = `<tr><td>${payment.extraInfo}</td></tr>`;
        }
        let paymentAmount = parseFloat(payment.sumPaid);
        if(payment.type === 'CASH'){
            paymentAmount = parseFloat(payment.cashPaid);
            extraInfo += `\
                <tr>\
                    <td>${CTR.translate('changeMoneySum')}</td>\
                    <td align="right">${CTR.displayPrice(parseFloat(payment.cashChange).toFixed(CTR.numberPrecision))}</td>\
                </tr>\
            `;
            if(payment.currencyCode !== null){
                let rate = payment?.currencyRate ?? false;
                paymentAmount = parseFloat(payment.sumPaid);
                if(rate !== false){
                    extraInfo += `\
                        <tr>\
                            <td>${payment.cashPaid.toFixed(CTR.numberPrecision)}${payment.currencyCode} ${CTR.translate('currency rate')}=${rate}</td>\
                        </tr>\
                    `;
                }
            }
        }

        if(CTR.positiveReturnLines){
            paymentAmount = Math.abs(paymentAmount);
        }

        return `\
            <tr>\
                <td>${CTR.translate(payment?.title || payment.type)}</td>\
                <td style="text-align: right">${CTR.displayPrice(paymentAmount.toFixed(CTR.numberPrecision))}</td>\
            </tr>\
            ${extraInfo}\
        `;
    },
    getTotalLine: (total, numberOfArticles, documentDiscountPercentage, documentDiscountPrice) => {
        let numberOfArticlesAbs = Math.abs(numberOfArticles);
        let lineTotal = parseFloat(total);
        if(CTR.positiveReturnLines){
            lineTotal = Math.abs(lineTotal);
        }
        let discount = '';
        if(documentDiscountPercentage !== ""){
            discount = documentDiscountPercentage * -1 + "%";
        }
        if(documentDiscountPrice !== ""){
            discount = documentDiscountPrice * -1 + CTR.currencySign;
        }
        return `\
            <table id="total" style="width: 100%">\
                <tr style="font-size: 20px;font-weight: bold">\
                    <td>${CTR.translate('Total')}</td>\
                    <td>${discount}</td>\
                    <td style="text-align: right">${CTR.displayPrice(lineTotal.toFixed(CTR.numberPrecision))}</td>\
                </tr>\
                <tr>\
                    <td></td>\
                    <td></td>\
                    <td style="text-align: right">\
                        ${numberOfArticlesAbs} ${numberOfArticlesAbs > 1 ? CTR.translate('Articles') : CTR.translate('Article')}\
                    </td>\
                </tr>\
            </table>\
        `;
    },
    getTaxLines: (products, vatRates) => {
        let taxTotals = CTR.getTaxTotals(products, vatRates);
        return `\
            <table id="taxTotals" style="width: 100%;">\
                <thead>\
                    <tr>\
                        <th style="text-align: left;">${CTR.translate('Tax')}</th>\
                        <th style="text-align: left">${CTR.translate('Tax total')}</th>\
                        <th style="text-align: right">${CTR.translate('Net total')}</th>\
                    </tr>\
                </thead>\
                <tbody>\
                    ${Object.keys(taxTotals).map((vatRateID) => {
                        let taxTotal = taxTotals[vatRateID];
                        return CTR.createTaxLine(taxTotal, vatRateID);
                    }).join('')}\
                </tbody>\
            </table>\
        `;
    },
    getTaxTotals: (products, vatRates) => {
        let taxTotals = products.reduce((acc, product) => {
            if(product.isUsedProduct()){
                return acc;
            }

            if(typeof acc[product.data.vatrateID] === "undefined"){
                acc[product.data.vatrateID] = {
                    rate: product.data.vatrate,
                    vat: product.rowVAT,
                    net: product.rowNetTotal
                }
            }else{
                acc[product.data.vatrateID].vat += product.rowVAT;
                acc[product.data.vatrateID].net += product.rowNetTotal;
            }
            return acc;
        }, {});

        for(let vatRateID in taxTotals){
            let vatRate = getVatRateByID(vatRates, vatRateID);
            let taxTotal = taxTotals[vatRateID];

            if(typeof vatRate?.components !== "undefined"){
                taxTotal.components = [];
                for(let component of vatRate.components){
                    taxTotal.components.push({
                        name: component.name,
                        total: (taxTotal.net * component.rate / 100).toFixed(3),
                        rate: component.rate
                    });
                }
            }
        }
        return taxTotals;
    },
    createTaxLine: (tax) => {
        let taxVat = tax.vat;
        let taxNet = tax.net;
        if(CTR.positiveReturnLines){
            taxVat = Math.abs(taxVat);
            taxNet = Math.abs(taxNet);
        }
        let componentLines = [];
        if(typeof tax.components !== "undefined"){
            componentLines = tax.components;
        }
        return `\
            <tr>\
                <td>${tax.rate}%</td>\
                <td>${CTR.displayPrice(taxVat.toFixed(CTR.numberPrecision))}</td>\
                <td style="text-align: right">${CTR.displayPrice(taxNet.toFixed(CTR.numberPrecision))}</td>\
            </tr>\
            ${componentLines.map(component => {
                return `\
                    <tr>\
                        <td align="left">${component.name}(${component.rate}%):</td>\
                        <td>${CTR.displayPrice(component.total)}</td>\
                    </tr>\
                `;
            }).join('')}
        `;
    },
    cutReceiptNumber: (receiptNumber) => {
        let cutReceiptNumber = false;
        if(receiptNumber !== false){
            let receiptNumberString = receiptNumber.toString();
            cutReceiptNumber = receiptNumberString.substring(receiptNumberString.length - 7);
        }
        return cutReceiptNumber;
    },
    getPOSInformationLines: (tillNumber, date, time, employee, storeNumber, receiptNumber=false) => {
        let formattedDate = moment(date).format('DD/MM/YYYY');
        let cutReceiptNumber = CTR.cutReceiptNumber(receiptNumber);
        return `\
            <div>${CTR.translate('Till')}: ${tillNumber} ${formattedDate} ${time}</div>\
            <div>${CTR.translate('Employee')}: ${employee} ${CTR.translate('Store')}: ${storeNumber} \
                ${cutReceiptNumber !== false ? `${CTR.translate('Tran')}: ${cutReceiptNumber}` : ''}</div>\
        `;
    },
    getBarcodeLine: (barcode) => {
        return new Promise((resolve) => {
            CTR.textToBase64Barcode(barcode).then((barcode) => {
                resolve(`\
                    <div id="barcode" style="text-align: center;padding:5px 0;">\
                        <img style="max-width: 100%; min-height: 40px" alt="barcode" src="${barcode}" />\
                    </div>\
                `);
            });
        });
    },
    generatedReceiptNumber: {
        fromNumber: false,
        generated: false
    },
    createUniqueReceiptNumber: (storeNumber, tillNumber, date, time, receiptNumber) => {
        console.log("CTR: createUniqueReceiptNumber", {
            generated: CTR.generatedReceiptNumber.generated,
            receiptNumber,
            fromNumber: CTR.generatedReceiptNumber.fromNumber
        });
        if(CTR.generatedReceiptNumber.fromNumber === receiptNumber){
            return CTR.generatedReceiptNumber.generated;
        }
        let formattedDate = moment(date + ' ' + time).format(CTR.dateFormatInReceiptNumber());
        let cutReceiptNumber = CTR.cutReceiptNumber(receiptNumber);
        let uniqueReceiptNumber = storeNumber.padStart(4, '0') + tillNumber.padStart(2, '0') + formattedDate + cutReceiptNumber;
        CTR.generatedReceiptNumber = {
            fromNumber: receiptNumber,
            generated: uniqueReceiptNumber,
            dateTime: moment(date + ' ' + time)
        };
        return uniqueReceiptNumber;
    },
    textToBase64Barcode: (text, qrCode=false) => {
        return new Promise((resolve) => {
            let canvas = document.createElement("canvas");
            if(CTR.displayQRCode() || qrCode){
                bwipjs.toCanvas(canvas, {
                    text,
                    bcid: "qrcode",
                    includetext: false,
                    textxalign:  'center'
                });
            }else{
                bwipjs.toCanvas(canvas, {
                    text,
                    bcid: "code128",
                    height: 7,
                    scale: 2,
                    includetext: false,
                    textxalign:  'center'
                });
            }
            setTimeout(() => {
                resolve(canvas.toDataURL("image/png"));
            },1);
        });
    },
    getDigitalReceipt: (receipt) => {
        return CTR.removeImages(receipt);
    },
    removeImages: (receipt) => {
        return receipt.replace(/<img[^>]*>/g,"");
    },
    addImages: (receipt) => {
        return new Promise(((resolve, reject) => {
            let parser = new DOMParser();
            let doc = parser.parseFromString(receipt, 'text/html');
            doc = CTR.addLogo(doc);
            CTR.addBarcode(doc).then((doc) => {
                resolve(doc.documentElement.outerHTML);
            });
        }));
    },
    addLogo: (receiptDoc) => {
        receiptDoc.getElementById('companyLogo').outerHTML = CTR.getLogo(CTR.logoBase64);
        return receiptDoc;

    },
    addBarcode: (receipt) => {
        return new Promise(((resolve, reject) => {
            let receiptNumberEl = receipt.getElementById('receiptNumber');
            if(typeof receiptNumberEl === "undefined" || receiptNumberEl === null){
                return resolve(receipt);
            }
            let receiptNumber = receiptNumberEl.innerText;
            console.log({receiptNumber});

            CTR.getBarcodeLine(receiptNumber, receipt).then((barcodeLine) => {
                receipt.getElementById('barcode').outerHTML = barcodeLine;
                resolve(receipt);
            });
        }));
    },
    getCardPayments: (payments) => {
        return `\
            <div style="width: 100%;">\
                ${payments.map((payment) => {
                    return CTR.createCardPaymentTable(payment);
                }).join('')}\
            </div>\
        `;
    },
    createCardPaymentTable: (payment) => {
        return `\
            <table>\
                <tbody>\
                    <tr>\
                        <td>${CTR.translate('Amount Paid')}:</td>\
                        <td>${CTR.displayPrice(payment.sumPaid)}</td>\
                    </tr>\
                    <tr>\
                        <td>${CTR.translate('Card Type')}:</td>\
                        <td>${payment.transaction.cardType}</td>\
                    </tr>\
                    <tr>\
                        <td>${CTR.translate('Card #')}:</td>\
                        <td>${payment.transaction.cardNumber}</td>\
                    </tr>\
                    <tr>\
                        <td>${CTR.translate('Expires')}:</td>\
                        <td>${CTR.getPaymentExpires(payment)}</td>\
                    </tr>\
                    <tr>\
                        <td>${CTR.translate('Ref #')}:</td>\
                        <td>${CTR.getPaymentRefNo(payment)}</td>\
                    </tr>\
                    <tr>\
                        <td>${CTR.translate('Authorization code')}:</td>\
                        <td>${CTR.getPaymentAuthCode(payment)}</td>\
                    </tr>\
                </tbody>\
            </table>\
        `;
    },
    displayPrice: (price) => {
        return `${formatDisplayPrice(price)}${CTR.currencySign}`;
    },
    getPaymentRefNo: (payment) => {
        if(typeof payment.transaction.refNo !== "undefined") {
            return payment.transaction.refNo;
        }
        return '';
    },
    getPaymentAuthCode: (payment) => {
        if(typeof payment.transaction.authCode !== "undefined") {
            return payment.transaction.authCode;
        }
        return '';
    },
    getPaymentExpires: (payment) => {
        return 'XX/XX';
    },
    createGiftReceipt: (receipt) => {
        let parser = new DOMParser();
        let doc = parser.parseFromString(receipt, 'text/html');

        doc.getElementById('receiptType').innerText = CTR.translate('Gift');
        let total = doc.getElementById('total');
        if(total !== null) total.remove();
        let payments = doc.getElementById('payments');
        if(payments !== null) payments.remove();
        let taxTotals = doc.getElementById('taxTotals');
        if(taxTotals !== null) taxTotals.remove();
        CTR.clearElementsByClass(doc,'itemPrice');
        let cardPayments = doc.getElementById('cardPayments');
        if(cardPayments !== null) cardPayments.remove();

        return doc.documentElement.outerHTML;
    },
    clearElementsByClass: (doc, className) => {
        var elements = doc.getElementsByClassName(className);
        while(elements.length > 0){
            elements[0].parentNode.removeChild(elements[0]);
        }
    },
    getReceiptFromAttributes: (attributes) => {
        if(typeof attributes === "undefined") return false;

        let receiptAttribute = attributes.find(attribute => attribute.attributeName === 'receiptHtml');
        if (typeof receiptAttribute === "undefined"){
            return false;
        }
        return decodeURIComponent(escape(atob(receiptAttribute.attributeValue)));
    },
    sendEmail: (email, receipt, documentNumber, message) => {
        return new Promise(async (resolve, reject) => {
            try {
                let primaryMailbox = await CTR.getPrimaryMailbox();
                let receiptPng = await CTR.createPng(receipt, 800);

                let url = CTR.emailAPIEndpoint + "api/v2/message";
                if(process.env.REACT_APP_USE_LOCAL_PROXY === "1"){
                    url = APP_URL + "postProxy?u=" + btoa(url)
                }

                let files = [{
                    //"contentType": "application/pdf",
                    "contentType": "image/png",
                    "disposition": "attachment",
                    "filename": "Receipt" + documentNumber + '.png',
                    "content": receiptPng
                }];

                if(window.AppConf.emailIncludeGiftReceipt){
                    let giftReceipt = CTR.createGiftReceipt(receipt);
                    let giftReceiptPng = await CTR.createPng(giftReceipt, 800);
                    files.push({
                        //"contentType": "application/pdf",
                        "contentType": "image/png",
                        "disposition": "attachment",
                        "filename": "GiftReceipt" + documentNumber + '.png',
                        "content": giftReceiptPng
                    })
                }

                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        clientCode: CTR.clientCode,
                        sessionKey: CTR.sessionKey
                    },
                    body: JSON.stringify({
                        subject: "Receipt",
                        type: "email",
                        files,
                        bodies: [
                            {
                                contentType: "text/plain",
                                content: base64Encode(message)
                            }
                        ],
                        contacts: [{
                            address: email,
                            //"name": "Customer",
                            type: "to"
                        },{
                            address: primaryMailbox,
                            name: "Decathlon",
                            type: "from"
                        }]
                    })
                }).then(response => {
                    if (response.status === 201) {
                        resolve(response);
                    } else {
                        response.json().then((res) => {
                            reject(res);
                        });
                    }
                }).catch(error => {
                    reject(error);
                });
            }catch (e){
                reject(e);
            }
        });
    },
    findMailbox: (records, text) => {
        let mailboxNames = ['Decathlon', CTR.clientCode];
        let getMailbox = (records, text) => records.findLast(record => record.name.indexOf(text) !== -1);
        let mailbox;

        for(let name of mailboxNames){
            mailbox = getMailbox(records, name);
            if(typeof mailbox !== "undefined"){
                break;
            }
        }
        if(typeof mailbox !== "undefined"){
            return mailbox.name;
        }else{
            return records[0].name;
        }
    },
    getPrimaryMailbox: () => {
        return new Promise(async (resolve, reject) => {
            if(typeof CTR.primaryMailbox !== "undefined"){
                resolve(CTR.primaryMailbox);
                return true;
            }
            let url = CTR.emailAPIEndpoint + "api/v2/mailbox?mailboxIsPrimary=true";
            if(process.env.REACT_APP_USE_LOCAL_PROXY === "1") {
                url = APP_URL + "proxy?u=" + btoa(url);
            }
            fetch(url, {
                method: 'GET',
                headers: {
                    clientCode: CTR.clientCode,
                    sessionKey: CTR.sessionKey
                }
            }).then(async response => {
                if (response.status === 200) {
                    let records = await response.json();
                    if(records.length > 0){
                        CTR.primaryMailbox = CTR.findMailbox(records);
                        resolve(CTR.primaryMailbox);
                    }else{
                        reject();
                    }
                } else {
                    reject();
                }
            }).catch(error => {
                reject();
            });
        });
    },
    createPng: (receiptHtml, width=CTR.receiptWidth()) => {
        return new Promise((resolve, reject) => {
            let iframe = document.createElement('iframe');
            iframe.style.width = `${CTR.receiptWidth()}px`;
            iframe.id = "receiptIframe";
            document.body.appendChild(iframe);
            let iframedoc=iframe.contentDocument||iframe.contentWindow.document;
            iframedoc.open();
            iframedoc.write(receiptHtml);
            iframedoc.close();
            iframedoc.body.style.fontSize = "12px";
            iframedoc.body.style.fontFamily = "sans-serif";
            setTimeout(function(){
                html2canvas(iframedoc.body, {
                    scale: width / CTR.receiptWidth(),
                    useCORS: true
                }).then(function(canvas) {
                    let myImage = canvas.toDataURL("image/png", 1);
                    document.getElementById('receiptIframe').remove();
                    resolve(myImage.split('data:image/png;base64,')[1]);
                });
            }, 10);
        });
    },
    getNameInitials: (name) => {
        return name.split(' ').reduce((acc, el) => {
            acc.push(el.slice(0,1));
            return acc;
        }, []).join(' ');
    },
    openBrowserPrint: function (html) {
        var printWin = window.open();
        if(printWin === null){
            alert("Allow pop-ups for browser print!");
            return;
        }
        printWin.document.write(html);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => {
            printWin.print();
            printWin.close();
        }, 1);
    }
};

CTR.createReceiptTime = false;

CTR.onCreateReceipt = (state) => {
    return new Promise((resolve, reject) => {
        let warehouse = state.rootReducer.warehouse;
        let pos = state.rootReducer.pos;
        const phone = pos.phone !== "" ? pos.phone : warehouse.phone !== "" ?  warehouse.phone : state.rootReducer.companyInfo.phone;
        let companyInformation = new CTR.companyInformation(
            state.rootReducer.companyInfo.name,
            warehouse.name,
            warehouse.street,
            warehouse.ZIPcode,
            warehouse.city,
            phone,
            state.rootReducer.companyInfo.VAT,
            pos.storeHours
        );
        const totalSum = getProductsTotalSum(state.rootReducer.productsInBasket);
        let isRefund = totalSum < 0;
        let receiptType = isRefund ? 'Refund' : 'Sale';
        let customer = state.rootReducer.customer.default ? undefined : state.rootReducer.customer;
        let tillNumber = getTillNumber(pos);
        let employeeInfo;
        if(state.rootReducer.transactionUser !== false){
            employeeInfo = getEmployeeByID(state.rootReducer.transactionUser.employeeID, state.rootReducer.employees);
            if(typeof employeeInfo === "undefined"){
                Sentry.captureException("Transaction user employee missing. UserID: " + state.rootReducer.transactionUser.userID + ", EmployeeID: " + state.rootReducer.transactionUser.employeeID);
            }
        }
        if(typeof employeeInfo === "undefined"){
            employeeInfo = getEmployeeByID(state.rootReducer.user.employeeID, state.rootReducer.employees);
        }

        if(typeof employeeInfo === "undefined"){
            employeeInfo = {
                firstName: "default",
                lastName: "host"
            }
        }

        let employee = CTR.getNameInitials(employeeInfo.firstName) + ' ' + CTR.getNameInitials(employeeInfo.lastName);

        let cardPayments;
        let cardPaymentsData = getCardPayments(state.payment.payments);

        if(cardPaymentsData.length > 0){
            let cardholderReceipts = cardPaymentsData.map(cardPayment => {
                return cardPayment?.cardholderReceipt || "";
            }).join("");
            if(cardholderReceipts !== ""){
                cardPayments = cardholderReceipts;
            }else{
                if(state.rootReducer.erplyConf.show_card_payments_on_receipt === "1"){
                    cardPayments = CTR.getCardPayments(cardPaymentsData);
                }
            }
        }

        let date = moment().format("YYYY-MM-DD");
        let time = CTR.createReceiptTime !== false ? CTR.createReceiptTime : moment().format("HH:mm:ss");

        let notes = getDocumentNotes(state.rootReducer.pluginRequestParameters, state.payment.payments);
        let memberFooter = typeof customer !== "undefined" ? (window?.AppConf?.decathlonMemberReceiptNotes || "") :
            (window?.AppConf?.decathlonNonMemberReceiptNotes || "");
        if(memberFooter !== ""){
            memberFooter += "<br>";
        }

        CTR.createReceipt(
            companyInformation,
            receiptType,
            state.rootReducer.receiptFiscalInfo,
            customer,
            getProductsInBasket(state),
            totalSum,
            state.payment.payments,
            tillNumber,
            date,
            time,
            employee,
            warehouse.code,
            state.rootReducer.nextInvoiceNo,
            memberFooter + state.rootReducer.erplyConf.receipt_footer,
            cardPayments,
            notes,
            state.rootReducer.erplyConf,
            {
                percentage: state.rootReducer.discount?.documentDiscountPercentage || '',
                price: state.rootReducer.discount?.documentDiscountPrice || ''
            },
            state.rootReducer.vatRates,
            state.rootReducer.receiptExtraLines,
            state.rootReducer.usedProductCustomer
        ).then(({receiptNumber, receipt}) => {
            CTR.lastReceipt = receipt;
            CTR.lastReceiptNumber = receiptNumber;
            resolve(btoa(unescape(encodeURIComponent(CTR.getDigitalReceipt(receipt)))));
        });
    });
};

CTR.handlePrintReceipt = (action, state) => {
    return new Promise((resolve, reject) => {
        if(action.payload?.duplicate || action.payload?.giftReceipt){
            let receiptHtml = CTR.getReceiptFromAttributes(action.payload.document.longAttributes);
            if(receiptHtml !== false){
                CTR.addImages(receiptHtml).then((receiptHtmlWithImages) => {
                    if(action.payload?.giftReceipt){
                        resolve(CTR.createGiftReceipt(receiptHtmlWithImages));
                    }else{
                        resolve(CTR.changeReceiptType(receiptHtmlWithImages, 'Duplicate'));
                    }
                });
            }else{
                reject(CTR.translate("Can't print the receipt"));
            }
        }else{
            resolve(CTR.lastReceipt);
        }
    });
};

CTR.handleSendEmail = (data, document, state) => {
    const message = state.rootReducer?.erplyConf?.['mailtemplate_default_' + state.rootReducer.erplyConf.default_language] ?? "Here is your receipt";
    if(state.rootReducer.lastInvoice?.id === data.id){
        return CTR.sendEmail(data.email, CTR.lastReceipt, CTR.lastReceiptNumber, message);
    }else{
        return new Promise((resolve, reject) => {
            let receiptHtml = CTR.getReceiptFromAttributes(document.longAttributes);
            if(receiptHtml !== false){
                CTR.addImages(receiptHtml).then((receiptHtmlWithImages) => {
                    CTR.sendEmail(data.email, CTR.changeReceiptType(receiptHtmlWithImages, 'Duplicate'), document.number, message).then(resolve, reject);
                });
            }else{
                reject(CTR.translate("Can't send the email"));
            }
        });
    }
};

CTR.applyDESpecifics = () => {
    CTR.fiscalDataPosition = 'down';
    CTR.getCompanyLines = (companyInformation, confParameters) => {
        return `\
            <div\
                id="companyInfo"\
                style="text-align: center;"\
            >\
                <div>${companyInformation.companyName}</div>\
                <div>${companyInformation.storeAddress}</div>\
                <div>${companyInformation.storePostalCode} ${companyInformation.storeCity}</div>\
                <div>www.decathlon.de</div>
            </div>\
        `;
    };
}

window.CTR = CTR;
export default CTR;
